import { supabase } from './supabase';

export type AppointmentType = 'pickup' | 'delivery';

export interface RouteOption {
  routeId: string;
  /**
   * Route__c.Id (master-route object) — NIET bruikbaar voor /route-planner,
   * die verwacht een Routes__c-dagroute-id (= routeId hierboven).
   */
  geplandeRouteId?: string;
  label: string;
  datum: string;
  datumISO?: string;
  tijdslot: string;
  capaciteit: number;
  dagNaam?: string;
  dagNummer?: number;
  maandNaam?: string;
  isFull?: boolean;
  /** Route-datum is vandaag — chauffeur is onderweg. Alleen gezet bij officeMode-lookup. */
  isVandaag?: boolean;
  /** Kantoor heeft "niet voorstellen" gezet. Alleen gezet bij officeMode-lookup. */
  isNietVoorstellen?: boolean;
  /** Optionele waarschuwingstekst, bv. "Chauffeur is onderweg". */
  waarschuwing?: string;
  /** Master-route naam (alleen gevuld bij bypass-postcodefilter lookup). */
  routeNaam?: string;
  /** Datum valt in het sluitingsvenster van het gebied (bv. brugafsluiting). Alleen zichtbaar op office/Spoed-paden — klant-paden filteren deze datums weg. */
  gebiedGesloten?: boolean;
  /** Gesloten_Reden__c van het gebied, voor de badge. */
  gebiedGeslotenReden?: string;
}

export interface ScheduledAppointment {
  label: string;
  vanaf: string | null;
  tot: string | null;
  isDefinitief: boolean;
}

export interface ScheduleResponse {
  ok: boolean;
  appointment: ScheduledAppointment | null;
}

export interface BumpCapacityResponse {
  ok: boolean;
  newRemainingCapacity: number | null;
}

export interface ActiveOrder {
  id: string;
  orderNumber: string;
  status: string;
  brand: string;
  hasPickup: boolean;
  hasDelivery: boolean;
  isPlannable: boolean;
}

async function authHeader(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

async function parseOrThrow(response: Response): Promise<any> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    const error = new Error(err.error || err.code || 'Request failed') as Error & {
      status?: number;
      code?: string;
    };
    error.status = response.status;
    error.code = err.code;
    throw error;
  }
  return response.json();
}

export async function fetchAvailableRoutes(
  orderId: string,
  type: AppointmentType,
  options?: { includeFull?: boolean; officeMode?: boolean; bypassPostcodeFilter?: boolean }
): Promise<RouteOption[]> {
  const headers = await authHeader();
  const qs = new URLSearchParams({ id: orderId, type });
  if (options?.includeFull) qs.set('includeFull', '1');
  // Office-app default: officeMode aan zodat de drawer ook vandaag-routes,
  // volle routes en niet_voorstellen-routes laat zien (badges in de UI).
  // Caller kan expliciet officeMode=false meegeven om classic-gedrag te krijgen.
  const officeMode = options?.officeMode ?? true;
  if (officeMode) qs.set('officeMode', '1');
  if (options?.bypassPostcodeFilter) qs.set('bypassPostcodeFilter', '1');
  const response = await fetch(`/api/orders/available-routes?${qs.toString()}`, { headers });
  const payload = await parseOrThrow(response);
  return Array.isArray(payload.routes) ? payload.routes : [];
}

export async function scheduleAppointment(
  orderId: string,
  routeId: string,
  type: AppointmentType,
  notes: string,
  options: {
    informerenKlant?: boolean;
    bypassPostcodeFilter?: boolean;
    bypassReden?: string;
    geenOpslagkosten?: boolean;
    redenGeenOpslagkosten?: string;
  } = {}
): Promise<ScheduleResponse> {
  const headers = await authHeader();
  const body: Record<string, unknown> = { orderId, routeId, type, notes };
  if (options.informerenKlant !== undefined) {
    body.informerenKlant = options.informerenKlant;
  }
  if (options.bypassPostcodeFilter) {
    body.bypassPostcodeFilter = true;
    body.bypassReden = options.bypassReden ?? '';
  }
  if (options.geenOpslagkosten !== undefined) {
    body.geenOpslagkosten = options.geenOpslagkosten;
    if (options.geenOpslagkosten) {
      body.redenGeenOpslagkosten = options.redenGeenOpslagkosten ?? '';
    }
  }
  const response = await fetch('/api/orders/schedule', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const payload = await parseOrThrow(response);
  return {
    ok: payload.ok === true,
    appointment: payload.appointment ?? null,
  };
}

export async function bumpCapacity(
  workOrderId: string,
  routeId: string,
  type: AppointmentType,
  delta: number,
  reason: string
): Promise<BumpCapacityResponse> {
  const headers = await authHeader();
  const response = await fetch('/api/routes/capacity', {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ workOrderId, routeId, type, delta, reason }),
  });
  const payload = await parseOrThrow(response);
  return {
    ok: payload.ok === true,
    newRemainingCapacity: typeof payload.newRemainingCapacity === 'number'
      ? payload.newRemainingCapacity
      : null,
  };
}

export async function fetchActiveOrders(accountId: string): Promise<ActiveOrder[]> {
  const headers = await authHeader();
  const qs = new URLSearchParams({ accountId });
  const response = await fetch(`/api/customers/active-orders?${qs.toString()}`, { headers });
  const payload = await parseOrThrow(response);
  return Array.isArray(payload.orders) ? payload.orders : [];
}
